import type { AceCtaTrackingType } from "../../../../common-deprecated/tracking/types";
import type {
    FinanceOptionType,
    PersonalizationType,
    PriceType,
} from "../../../../common-deprecated/types/CommonTypes";
import { trackEvent, updateTrackingBaseLayer } from "../../../../common-deprecated/utils/tracking";
import type { BuildAndBuyStateType } from "../redux/store";
import { NavStep } from "../../shared-logic/utils/Navigation";

type StepLabelType =
    | "select-model"
    | "select-submodel"
    | "select-engine"
    | "select-model-engine"
    | "customize"
    | "services"
    | "summary"
    | "buy-overview"
    | "reservation-overview"
    | "leasing-overview";

const stepTrackingValueMap: Record<NavStep, StepLabelType> = {
    [NavStep.Model]: "select-model",
    [NavStep.ModelEngine]: "select-model-engine",
    [NavStep.Engine]: "select-engine",
    [NavStep.Submodels]: "select-submodel",
    [NavStep.Grades]: "select-model", // same as toyota's model step, as both are actually steps to select a grade
    [NavStep.Customize]: "customize",
    [NavStep.Services]: "services",
    [NavStep.Summary]: "summary",
    [NavStep.Buy]: "buy-overview",
    [NavStep.Reserve]: "reservation-overview",
    [NavStep.Lease]: "leasing-overview",
};

// ----------------------------------------------------------------------
// Loading build / workflow events
// ----------------------------------------------------------------------

const getLowestCarPrice = <T extends { price: PriceType }>(items: T[]): number | undefined =>
    items.reduce(
        (cheapest, item) => (!cheapest || item.price.discount < cheapest ? item.price.discount : cheapest),
        undefined as number | undefined,
    );

export const updateTrackingLayerVersioning = (): void => {
    updateTrackingBaseLayer({
        page: {
            ...((window.tngaDataLayer?.page as Record<string, unknown>) || {}),
            versions: {
                ...((window.tngaDataLayer?.page?.versions as Record<string, unknown>) || {}),
                "dxp-webcarconfig": commithash,
            },
        },
    });
};

/** used on the engine/grades steps */
export const trackStepLoadPreCustomize = (state: BuildAndBuyStateType): void => {
    const { step } = state.navigation;
    const { showMergedModelStep } = state.bnbSettings;
    const label = showMergedModelStep ? "select-model-engine" : stepTrackingValueMap[step];
    const { currencyCode } = state.commonSettings.intl;

    const itemsMap: Partial<Record<NavStep, { price: PriceType }[]>> = {
        [NavStep.Model]: state.model.grades,
        [NavStep.Engine]: state.grade.motorizations,
        [NavStep.Submodels]: state.submodels.list,
        [NavStep.Grades]: state.grades.list,
    };
    const cheapestPrice = getLowestCarPrice(itemsMap[step] || []);

    const modelId = step === NavStep.Engine ? state.grade.model.id : state.model.id || state.grades?.modelId;
    const productInfo = showMergedModelStep
        ? {
              tmeModelId: state.model.internalCode.toLowerCase(),
              modelName: state.model?.name,
          }
        : step === NavStep.Engine
          ? {
                tmeModelId: state.grade.model.code.toLowerCase(),
                modelName: state.grade.model.name,
                productName: `${state.grade.model.name} ${state.grade.name} ${state.grade.bodyType.name}`,
                gradeId: state.grade.id,
                gradeName: state.grade.name,
                bodytypeId: state.grade.bodyType.id,
                bodytypeName: state.grade.bodyType.name,
                price: cheapestPrice?.toFixed(2),
                currencyCode,
            }
          : {
                tmeModelId: (state.model.internalCode || state.grades?.modelCode || "").toLowerCase(),
                modelName: state.model?.name || state.grades?.modelName,
                price: cheapestPrice?.toFixed(2),
                currencyCode,
            };

    updateTrackingBaseLayer({
        configuration: {
            type: "carconfig",
            modelId,
            accessories: 0,
            options: 0,
            packs: 0,
            services: 0,
        },
        product: [
            {
                category: "cars",
                subCategory: "new-cars",
                productInfo,
            },
        ],
    });
    trackEvent(
        {
            name: "carconfigstep",
            component: "carconfig",
            group: "new-cars",
            action: "workflow",
            label,
        },
        {
            workflow: {
                name: "carconfigurator",
                step: label,
                conversion: 0,
            },
        },
        {
            carconfigStep: label,
        },
    );
};

const getArrayFilledFlag = (items: unknown[]): 0 | 1 => (items.length ? 1 : 0);

/** used on all steps from customize onwards */
export const trackStepLoadPostCustomize = (state: BuildAndBuyStateType): void => {
    const { navigation, car, basket } = state;
    const label = stepTrackingValueMap[navigation.step];

    const accessories = basket.extras.filter((extra) => extra.type === "accessory") || [];
    const options = basket.extras.filter((extra) => extra.type === "option") || [];
    const isHybrid = car.motorization.engine.hybrid ? 1 : 0;
    const { currencyCode } = state.commonSettings.intl;

    updateTrackingBaseLayer({
        configuration: {
            type: "carconfig",
            modelId: car.model.id,
            carPrice: car.price.discount.toFixed(2),
            totalPrice: basket.total.discount.toFixed(2),
            isHybrid,
            accessories: getArrayFilledFlag(accessories),
            options: getArrayFilledFlag(options),
            packs: getArrayFilledFlag(basket?.packs || []),
            services: getArrayFilledFlag(basket.financePacks || []),
        },
        product: [
            {
                category: "cars",
                subCategory: "new-cars",
                productInfo: {
                    productId: car.id,
                    productName: `${state.car.model.name} ${state.car.grade.name} ${state.car.body.name}`,
                    price: car.price.discount.toFixed(2),
                    currencyCode,
                    tmeModelId: car.model.internalCode.toLowerCase(),
                    modelName: car.model.name,
                    gradeId: car.grade.id,
                    gradeName: car.grade.name,
                    engineId: `${car.motorization.engine.internalCode}${car.motorization.transmission.internalCode}${car.motorization.wheeldrive.internalCode}`,
                    engineName: car.motorization.engine.name,
                    engineType: car.motorization.engine.typeId,
                    engineIsHybrid: isHybrid,
                    bodytypeId: car.body.id,
                    bodytypeName: car.body.name,
                    transmissionId: car.motorization.transmission.id,
                    transmissionName: car.motorization.transmission.name,
                },
            },
            {
                category: "colours",
                subCategory: "exterior",
                productInfo: {
                    productId: basket.colour.code,
                    productName: basket.colour.name,
                    price: basket.colour.price.discount.toFixed(2),
                    currencyCode,
                },
            },
            {
                category: "upholsteries",
                subCategory: "interior",
                productInfo: {
                    productId: basket.upholstery.id,
                    productName: basket.upholstery.name,
                    price: basket.upholstery.price.discount.toFixed(2),
                    currencyCode,
                },
            },
            {
                category: "wheels",
                productInfo: {
                    productId: basket.wheel.id,
                    productName: basket.wheel.name,
                    price: basket.wheel.price.discount.toFixed(2),
                    currencyCode,
                },
            },
            ...basket.packs.map((pack) => ({
                category: "packs",
                productInfo: {
                    productId: pack.id,
                    productName: pack.name,
                    price: pack.price.discount.toFixed(2),
                    currencyCode,
                },
            })),
            ...[...basket.extras, ...basket.financePackExtras].map((extra) => ({
                category: "accessories",
                subCategory: extra.category.root?.code === "EXT" ? "exterior" : "interior",
                productInfo: {
                    productId: extra.id,
                    productName: extra.name,
                    price: extra.price.discount.toFixed(2),
                    currencyCode,
                },
            })),
            ...[...basket.serviceProducts, ...basket.financePackServiceProducts, ...basket.financePacks].map(
                (service) => ({
                    category: "services",
                    productInfo: {
                        productId: service.id,
                        productName: service.name,
                        price: service.price.discount.toFixed(2),
                        currencyCode,
                    },
                }),
            ),
        ],
    });
    trackEvent(
        {
            name: navigation.step === NavStep.Summary ? "configdetailsevent" : "carconfigstep",
            component: "carconfig",
            group: "new-cars",
            action: "workflow",
            label,
        },
        {
            workflow: {
                name: "carconfigurator",
                step: label,
                conversion: [NavStep.Customize, NavStep.Summary, NavStep.Buy].includes(navigation.step) ? 1 : 0,
            },
        },
        {
            carconfigStep: label,
        },
    );
};

// ----------------------------------------------------------------------
// Navigation events
// ----------------------------------------------------------------------

export const trackNavMobileOpen = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-navigation",
            label: "navigation-menu-open",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackNavBackStep = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-navigation",
            label: "previous-step",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackNavBackOverview = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-navigation",
            label: "grade-overview",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

/** Used in the body when an action advances the build step as well */
export const trackNavGoToStep = (current: NavStep, destination: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-navigation",
            label: stepTrackingValueMap[destination],
        },
        null,
        { carconfigStep: stepTrackingValueMap[current] },
    );
};

// ----------------------------------------------------------------------
// Action events
// ----------------------------------------------------------------------

export const trackAddCompare = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "add-compare",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackAddCompareProduct = (step: NavStep, gradeName: string, productNumber: number): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `add-compare-product-${gradeName}-${productNumber}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackChooseEngine = (step: NavStep, gradeName: string, productNumber: number): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `choose-engine-product-${gradeName}-${productNumber}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackFinanceToggle = (
    step: NavStep,
    financeOption: FinanceOptionType,
    action: "cc-action" | "cc-footer" | "cc-modal",
): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action,
            label: `change-price-display-${financeOption}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

/** Naming is a bit confusing here, this event is used together with the changeGrade label, that's expected */
export const trackChangeModel = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "change-model",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackEngineViewEquipment = (step?: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "view-grade-equipment",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step || NavStep.Engine] },
    );
};

export const trackEngineViewEquipmentWithGradeName = (gradeName: string, step?: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `view-grade-equipment-${gradeName}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step || NavStep.Engine] },
    );
};

export const trackEngineViewInfo = (step?: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "view-engine-info",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step || NavStep.Engine] },
    );
};

/** only certain modals should have a tracked cancel button  */
export const trackCancelModal = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-modal",
            label: "cancel",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

/** only certain modals should have a tracked close button  */
export const trackCloseModal = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-modal",
            label: "close",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackConfigurationCodeOpen = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "display-configuration-code",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackBasketMobileOpen = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "basket-panel-open",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackWinterWheelsOpen = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "winter-set-panel-open",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackWinterWheelsCancel = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "winter-set-cancel",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackWinterWheelsToggle = (enable: boolean): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: enable ? "preview-winter-wheels-enable" : "preview-winter-wheels-disable",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

/** Changing your car config with an item from the carhero personalization slider */
export const trackCustomizeFullscreen = (tab: PersonalizationType): void => {
    const valueMap: Record<PersonalizationType, string> = {
        colour: "full-screen-colours",
        wheel: "full-screen-wheels",
        upholstery: "full-screen-interior",
    };
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: valueMap[tab],
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackCustomizeExt360Spin = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "360-spin-ext",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackCustomizeInt360Spin = (step: NavStep = NavStep.Customize): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "360-spin-int",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackCustomizeIntGallery = (step?: NavStep, showMergedModelStep?: boolean): void => {
    const computedStep =
        (step === NavStep.Model && showMergedModelStep ? NavStep.ModelEngine : step) || NavStep.Customize;
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "browse-interior-gallery",
        },
        null,
        { carconfigStep: stepTrackingValueMap[computedStep] },
    );
};

export const trackPackViewFeatures = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "view-pack-features",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackExtrasShow = (
    step: NavStep,
    label: "show-all-extra-exterior" | "show-all-extra-interior" | "show-all-extra-packs",
): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackExtrasOpenFilter = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "open-filter-extras",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackExtrasApplyFilter = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "filter-extras",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackExtrasFirstSearch = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "search-extras",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

export const trackInPageCalculator = (
    action: "cc-action" | "cc-cta" | "cc-panel" | "cc-footer",
    label:
        | "open-inpage-finance-calculator"
        | "close-inpage-finance-calculator"
        | "open-inpage-insurance-calculator"
        | "close-inpage-insurance-calculator"
        | "anchor-add-finance-calculator"
        | "anchor-edit-finance-calculator"
        | "anchor-add-insurance-calculator"
        | "anchor-edit-insurance-calculator",
    step: NavStep,
): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action,
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

// CarConfigStep deviates from the norm.
export const trackClickedLink = (carconfigStep: "why-insurance" | "finance-benefits"): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "click-link",
        },
        null,
        { carconfigStep },
    );
};

export const trackChangeCustomisation = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: "change-customisation",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );
};

export const trackExpandSpecCategory = (label: string): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );
};

// ----------------------------------------------------------------------
// CTA events
// ----------------------------------------------------------------------

export const trackCtaCheckout = (
    step: NavStep,
    label: "online-retailing-checkout" | "online-retailing-checkout-reservation" | "online-retailing-checkout-leasing",
): void =>
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );

export const trackCtaSaveAccount = (): void =>
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-cta",
            label: "save-config",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );

export const trackCtaCalculator = (
    step: NavStep,
    label: "finance-calculator" | "insurance-calculator" | "tradein-calculator",
): void =>
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );

export const trackCtaMain = (step: NavStep, label: AceCtaTrackingType): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

/**
 * This should only be used when the custom CTA type is used and a custom tracking type is not set (will be implemented in OR-7633)
 */
export const trackCtaCustom = (step: NavStep, label: AceCtaTrackingType | string): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

// ----------------------------------------------------------------------
// Customize events (not only on customize step)
// ----------------------------------------------------------------------

export const trackCustomizeConfigItem = (
    step: NavStep,
    label:
        | "add-extra"
        | "add-extra-popular"
        | "remove-extra"
        | "add-services"
        | "remove-services"
        | "remove-winter-set",
): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-customize",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackCustomizeWinterWheel = (includeInstallation: boolean): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-customize",
            label: includeInstallation ? "add-winter-set-included" : "add-winter-set-not-included",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

/** Changing your car config with an item from the carhero personalization slider */
export const trackCustomizePersonalizationItem = (tab: PersonalizationType): void => {
    const valueMap: Record<PersonalizationType, string> = {
        colour: "select-colours",
        wheel: "select-wheels",
        upholstery: "select-interior",
    };
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-customize",
            label: valueMap[tab],
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

// ----------------------------------------------------------------------
// Tab events
// ----------------------------------------------------------------------

/** Changing the active tab in the carhero personalization panel */
export const trackTabPersonalization = (
    label: "customize-colours" | "customize-wheels" | "customize-interior",
): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-tab",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Customize] },
    );
};

// ----------------------------------------------------------------------
// Panel / Basket events
// ----------------------------------------------------------------------

export const trackBasketExpandPanel = (step: NavStep, label: "expand-packs" | "expand-extras"): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-panel",
            label,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackBasketRemoveExtra = (step: NavStep): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-panel",
            label: "remove-extra",
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackSummaryRemoveExtra = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-panel",
            label: "remove-extra",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );
};

export const trackEtaOpen = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-panel",
            label: "eta-panel-open",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );
};

export const trackEtaOpenSelect = (): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-panel",
            label: "eta-panel-select-car",
        },
        null,
        { carconfigStep: stepTrackingValueMap[NavStep.Summary] },
    );
};

// ----------------------------------------------------------------------
// Merged Model Engine Events
// ----------------------------------------------------------------------

export const trackSelectEngine = (step: NavStep, gradeName: string): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `expand-engine-${gradeName}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackBrowseEngine = (step: NavStep, gradeName: string): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `browse-engine-${gradeName}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};

export const trackShowInterior = (step: NavStep, gradeName: string): void => {
    trackEvent(
        {
            name: "carconfigevent",
            component: "carconfig",
            group: "new-cars",
            action: "cc-action",
            label: `view-interior-${gradeName}`,
        },
        null,
        { carconfigStep: stepTrackingValueMap[step] },
    );
};
